/**
 * Executive Briefing Page - ENHANCED VERSION
 * Comprehensive executive-level dashboard with deep insights
 *
 * Features:
 * - Detailed module-by-module breakdown
 * - Cross-module analytics and comparisons
 * - Data volume metrics
 * - Trend visualizations
 * - Prioritized recommendations per module
 * - Risk assessment heatmap
 * - Drill-down capability to individual modules
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Button,
  Spin,
  Alert,
  Divider,
  Timeline,
  Progress,
  Empty,
  Table,
  Collapse,
  Badge,
  Space,
  Tooltip,
  Switch
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  ReloadOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  TrophyOutlined,
  FallOutlined,
  RiseOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
  EyeOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import execute from '../../hooks/execute';
import { protectedResources } from '../../config/msalConfig';
import BriefingExport from './components/BriefingExport';
import { useUser } from '../../components/UserContext';
import ExecutivePresentationView from './ExecutivePresentationView';

const { Panel } = Collapse;

const ExecutiveBriefingPage = () => {
  const { briefingId } = useParams();
  const navigate = useNavigate();
  const { userDetails } = useUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [moduleDetails, setModuleDetails] = useState([]);
  const [viewMode, setViewMode] = useState('presentation'); // 'presentation' or 'dashboard'

  /**
   * Fetch briefing data
   */
  useEffect(() => {
    if (briefingId) {
      fetchBriefing();
    }
  }, [briefingId]);

  const fetchBriefing = async () => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = `${protectedResources.api.baseEndPoint}/v1/executive/briefing/${briefingId}`;
      const response = await execute('GET', endpoint);

      if (response.success) {
        setBriefing(response.data);
        await fetchModuleBreakdown(response.data);
      } else {
        setError(response.message || 'Failed to load briefing');
      }
    } catch (err) {
      console.error('[ExecutiveBriefing] Error fetching briefing:', err);
      setError(err.message || 'Failed to load briefing');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch detailed module breakdown
   */
  const fetchModuleBreakdown = async (briefingData) => {
    try {
      const modulesCovered = briefingData.ModulesCovered || [];

      // Fetch module summaries for each module
      const details = await Promise.all(
        modulesCovered.map(async (moduleId) => {
          try {
            const endpoint = `${protectedResources.api.baseEndPoint}/digest/module/${moduleId}?limit=1`;
            const response = await execute('GET', endpoint);

            if (response.success && response.data && response.data.length > 0) {
              const summary = response.data[0];
              return {
                moduleId,
                moduleName: summary.ModuleName || userDetails?.Modules?.find(m => m.ModuleId === moduleId)?.ModuleName || `Module ${moduleId}`,
                healthScore: summary.HealthScore || 0,
                pageCount: summary.PageCount || 0,
                reportCount: summary.ReportCount || 0,
                summary: summary.SummaryContent || '',
                generatedAt: summary.GeneratedAt,
                status: summary.Status
              };
            }

            // Fallback if no digest found
            const module = userDetails?.Modules?.find(m => m.ModuleId === moduleId);
            return {
              moduleId,
              moduleName: module?.ModuleName || `Module ${moduleId}`,
              healthScore: 0,
              pageCount: 0,
              reportCount: 0,
              summary: 'No recent analysis available',
              status: 'N/A'
            };
          } catch (err) {
            console.error(`Error fetching module ${moduleId}:`, err);
            return {
              moduleId,
              moduleName: `Module ${moduleId}`,
              healthScore: 0,
              pageCount: 0,
              reportCount: 0,
              summary: 'Error loading data',
              status: 'ERROR'
            };
          }
        })
      );

      setModuleDetails(details.sort((a, b) => b.healthScore - a.healthScore));
    } catch (err) {
      console.error('[ExecutiveBriefing] Error fetching module breakdown:', err);
    }
  };

  /**
   * Get risk level color
   */
  const getRiskColor = (level) => {
    const colors = {
      LOW: 'green',
      MEDIUM: 'gold',
      HIGH: 'orange',
      CRITICAL: 'red'
    };
    return colors[level] || 'blue';
  };

  /**
   * Get health color based on score
   */
  const getHealthColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    if (score >= 40) return '#ff7a45';
    return '#ff4d4f';
  };

  /**
   * Get risk icon
   */
  const getRiskIcon = (level) => {
    const icons = {
      LOW: <CheckCircleOutlined />,
      MEDIUM: <WarningOutlined />,
      HIGH: <ExclamationCircleOutlined />,
      CRITICAL: <ExclamationCircleOutlined />
    };
    return icons[level] || <CheckCircleOutlined />;
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" tip="Loading comprehensive executive briefing..." />
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div className="p-6">
        <Alert
          message="Unable to load briefing"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={fetchBriefing}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  /**
   * Render empty state
   */
  if (!briefing) {
    return (
      <div className="p-6">
        <Empty description="Briefing not found" />
      </div>
    );
  }

  /**
   * Parse JSON fields
   */
  const keyMetrics = briefing.KeyMetrics
    ? typeof briefing.KeyMetrics === 'string'
      ? JSON.parse(briefing.KeyMetrics)
      : briefing.KeyMetrics
    : [];

  const trends = briefing.Trends
    ? typeof briefing.Trends === 'string'
      ? JSON.parse(briefing.Trends)
      : briefing.Trends
    : [];

  const recommendations = briefing.Recommendations
    ? typeof briefing.Recommendations === 'string'
      ? JSON.parse(briefing.Recommendations)
      : briefing.Recommendations
    : [];

  const totalPages = moduleDetails.reduce((sum, m) => sum + (m.pageCount || 0), 0);
  const totalReports = moduleDetails.reduce((sum, m) => sum + (m.reportCount || 0), 0);
  const topPerformer = moduleDetails[0];
  const needsAttention = moduleDetails.filter(m => m.healthScore < 60);

  /**
   * Module comparison table columns
   */
  const moduleColumns = [
    {
      title: 'Rank',
      key: 'rank',
      width: 70,
      render: (_, __, index) => (
        <div className="text-center">
          {index === 0 ? <TrophyOutlined style={{ color: '#ffd700', fontSize: 20 }} /> : <span className="text-gray-500 font-bold">{index + 1}</span>}
        </div>
      )
    },
    {
      title: 'Module',
      dataIndex: 'moduleName',
      key: 'moduleName',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-xs text-gray-500">{record.reportCount} reports • {record.pageCount} pages analyzed</div>
        </div>
      )
    },
    {
      title: 'Health Score',
      dataIndex: 'healthScore',
      key: 'healthScore',
      width: 200,
      sorter: (a, b) => a.healthScore - b.healthScore,
      render: (score) => (
        <div>
          <Progress
            percent={score}
            size="small"
            strokeColor={getHealthColor(score)}
            format={(percent) => <span className="font-bold">{percent}/100</span>}
          />
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => {
        if (record.healthScore >= 80) return <Tag color="success">Excellent</Tag>;
        if (record.healthScore >= 60) return <Tag color="warning">Good</Tag>;
        if (record.healthScore >= 40) return <Tag color="orange">Fair</Tag>;
        return <Tag color="error">Needs Attention</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate(`/dashboard`)} // Could navigate to module-specific view
        >
          View Details
        </Button>
      )
    }
  ];

  /**
   * Render main content
   */
  // If presentation mode, render full-screen presentation
  if (viewMode === 'presentation') {
    return (
      <ExecutivePresentationView
        briefingData={briefing}
        onClose={() => navigate('/executive-briefing')}
      />
    );
  }

  // Otherwise render dashboard view
  return (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-ubuntu font-bold text-[#007ab9] mb-2">
              {briefing.Title || 'Executive Briefing'}
            </h1>
            <div className="flex items-center gap-4 text-gray-600">
              <span className="flex items-center gap-2">
                <CalendarOutlined />
                {new Date(briefing.GeneratedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
              <Tag color={getRiskColor(briefing.RiskLevel)} icon={getRiskIcon(briefing.RiskLevel)}>
                {briefing.RiskLevel} RISK
              </Tag>
              <Tag color="blue">{briefing.BriefingType?.replace('_', ' ')}</Tag>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => setViewMode('presentation')}
              style={{ background: '#F16622', borderColor: '#F16622' }}
            >
              Presentation View
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchBriefing}>
              Refresh
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => setShowExportModal(true)}
            >
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Executive Summary KPIs */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card className="text-center">
            <Statistic
              title="Overall Health Score"
              value={briefing.OverallHealthScore || 0}
              suffix="/ 100"
              valueStyle={{ color: getHealthColor(briefing.OverallHealthScore || 0), fontSize: 32 }}
            />
            <Progress
              percent={briefing.OverallHealthScore || 0}
              strokeColor={getHealthColor(briefing.OverallHealthScore || 0)}
              showInfo={false}
              className="mt-2"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Modules Analyzed"
              value={briefing.ModuleCount || 0}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div className="text-xs text-gray-500 mt-2">
              {totalReports} reports • {totalPages} pages
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Top Performer"
              value={topPerformer?.moduleName || 'N/A'}
              valueStyle={{ fontSize: 16, color: '#52c41a' }}
              prefix={<TrophyOutlined />}
            />
            <div className="text-xs text-gray-500 mt-2">
              Score: {topPerformer?.healthScore || 0}/100
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Needs Attention"
              value={needsAttention.length}
              valueStyle={{ color: needsAttention.length > 0 ? '#ff4d4f' : '#52c41a' }}
              prefix={<WarningOutlined />}
              suffix={`/ ${briefing.ModuleCount}`}
            />
            <div className="text-xs text-gray-500 mt-2">
              Modules below 60% health
            </div>
          </Card>
        </Col>
      </Row>

      {/* Executive Summary */}
      <Card title={
        <span className="text-lg font-bold">
          <FileTextOutlined className="mr-2" />
          Executive Summary
        </span>
      } className="mb-6">
        <div className="text-gray-700 leading-relaxed whitespace-pre-line text-base">
          {briefing.ExecutiveSummary || 'No summary available'}
        </div>
      </Card>

      {/* Module Performance Comparison */}
      <Card
        title={
          <span className="text-lg font-bold">
            <DatabaseOutlined className="mr-2" />
            Module Performance Breakdown
          </span>
        }
        className="mb-6"
      >
        <Table
          columns={moduleColumns}
          dataSource={moduleDetails}
          rowKey="moduleId"
          pagination={false}
          size="middle"
        />
      </Card>

      {/* Detailed Module Insights */}
      <Card
        title={
          <span className="text-lg font-bold">
            <FileTextOutlined className="mr-2" />
            Detailed Module Insights
          </span>
        }
        className="mb-6"
      >
        <Collapse accordion>
          {moduleDetails.map((module) => (
            <Panel
              header={
                <div className="flex items-center justify-between pr-4">
                  <span className="font-medium">{module.moduleName}</span>
                  <Space>
                    <Tag color={getHealthColor(module.healthScore)}>
                      {module.healthScore}/100
                    </Tag>
                    <span className="text-xs text-gray-500">
                      {module.reportCount} reports • {module.pageCount} pages
                    </span>
                  </Space>
                </div>
              }
              key={module.moduleId}
            >
              <div className="bg-gray-50 p-4 rounded">
                <Row gutter={16}>
                  <Col span={24}>
                    <h4 className="font-medium mb-2">Summary</h4>
                    <p className="text-gray-700 mb-4">{module.summary}</p>
                  </Col>
                  <Col span={12}>
                    <div className="mb-2">
                      <strong>Health Score:</strong>
                      <Progress
                        percent={module.healthScore}
                        strokeColor={getHealthColor(module.healthScore)}
                        className="ml-2"
                        style={{ maxWidth: 200 }}
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div>
                      <strong>Data Volume:</strong>
                      <div className="text-gray-600">
                        {module.reportCount} reports analyzed across {module.pageCount} pages
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Panel>
          ))}
        </Collapse>
      </Card>

      {/* Key Metrics Grid */}
      {keyMetrics && keyMetrics.length > 0 && (
        <Card title={
          <span className="text-lg font-bold">
            <RiseOutlined className="mr-2" />
            Key Performance Metrics
          </span>
        } className="mb-6">
          <Row gutter={[16, 16]}>
            {keyMetrics.map((metric, index) => (
              <Col xs={24} sm={12} md={8} lg={6} key={index}>
                <Card size="small" className="bg-gray-50">
                  <Statistic
                    title={metric.metric || metric.label || metric.name}
                    value={metric.value}
                    suffix={metric.unit || ''}
                    prefix={
                      metric.trend === 'up' ? (
                        <ArrowUpOutlined style={{ color: '#52c41a' }} />
                      ) : metric.trend === 'down' ? (
                        <ArrowDownOutlined style={{ color: '#ff4d4f' }} />
                      ) : null
                    }
                    valueStyle={{
                      color:
                        metric.trend === 'up'
                          ? '#52c41a'
                          : metric.trend === 'down'
                            ? '#ff4d4f'
                            : '#1890ff',
                      fontSize: 20
                    }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Trends and Recommendations */}
      <Row gutter={16}>
        {/* Trends */}
        {trends && trends.length > 0 && (
          <Col xs={24} lg={12}>
            <Card
              title={
                <span className="text-lg font-bold">
                  <RiseOutlined className="mr-2" />
                  Trends & Insights
                </span>
              }
              className="mb-6"
              style={{ height: '100%' }}
            >
              <Timeline>
                {trends.map((trend, index) => (
                  <Timeline.Item
                    key={index}
                    dot={
                      trend.trend === 'improving' ? <RiseOutlined style={{ color: '#52c41a', fontSize: 16 }} /> :
                        trend.trend === 'declining' ? <FallOutlined style={{ color: '#ff4d4f', fontSize: 16 }} /> :
                          null
                    }
                    color={
                      trend.trend === 'improving' || trend.type === 'positive'
                        ? 'green'
                        : trend.trend === 'declining' || trend.type === 'negative'
                          ? 'red'
                          : 'blue'
                    }
                  >
                    <div className="font-medium text-base">{trend.moduleName || trend.title || trend.category}</div>
                    <div className="text-gray-600 mt-1">
                      {trend.description || trend.insight}
                    </div>
                    {trend.change && (
                      <Tag color={trend.change > 0 ? 'success' : 'error'} className="mt-2">
                        {trend.change > 0 ? '+' : ''}{trend.change} points
                      </Tag>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          </Col>
        )}

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <Col xs={24} lg={12}>
            <Card
              title={
                <span className="text-lg font-bold">
                  <CheckCircleOutlined className="mr-2" />
                  Strategic Recommendations
                </span>
              }
              className="mb-6"
              style={{ height: '100%' }}
            >
              <div className="space-y-4">
                {recommendations.map((rec, index) => (
                  <div key={index} className="border-l-4 pl-4 py-3 rounded-r" style={{ borderColor: getRiskColor(rec.priority) }}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {rec.priority && (
                            <Tag color={getRiskColor(rec.priority)}>
                              {rec.priority} PRIORITY
                            </Tag>
                          )}
                        </div>
                        <div className="font-medium text-gray-800 text-base">
                          {rec.action || rec.title || rec.recommendation}
                        </div>
                        <div className="text-sm text-gray-600 mt-2">
                          {rec.rationale || rec.description}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* Data Coverage Summary */}
      <Card className="mb-6">
        <Row gutter={16}>
          <Col span={24}>
            <h3 className="text-lg font-bold mb-4">Data Coverage & Analysis Scope</h3>
          </Col>
          <Col xs={24} md={8}>
            <Statistic
              title="Total Modules Analyzed"
              value={briefing.ModuleCount || 0}
              prefix={<DatabaseOutlined />}
            />
          </Col>
          <Col xs={24} md={8}>
            <Statistic
              title="Total Reports Processed"
              value={totalReports}
              prefix={<FileTextOutlined />}
            />
          </Col>
          <Col xs={24} md={8}>
            <Statistic
              title="Total Pages Analyzed"
              value={totalPages}
              prefix={<FileTextOutlined />}
            />
          </Col>
        </Row>
        <Divider />
        <div className="text-sm text-gray-600">
          This executive briefing represents a comprehensive analysis across {briefing.ModuleCount} healthcare modules,
          processing {totalReports} detailed reports spanning {totalPages} pages of data.
          The analysis period covers {briefing.AnalysisStartDate && briefing.AnalysisEndDate ?
            `${new Date(briefing.AnalysisStartDate).toLocaleDateString()} to ${new Date(briefing.AnalysisEndDate).toLocaleDateString()}`
            : 'the last 30 days'}.
        </div>
      </Card>

      {/* Export Modal */}
      <BriefingExport
        visible={showExportModal}
        onClose={() => setShowExportModal(false)}
        briefingId={briefingId}
        briefingTitle={briefing.Title}
      />
    </div>
  );
};

export default ExecutiveBriefingPage;
